// Saved per-answer marks are the source of truth. Reading a result never re-runs AI.
const finiteNumber = value => {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (typeof value === 'string' && !value.trim()) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};
const roundMark = value => Math.round((value + Number.EPSILON) * 100) / 100;
const nonnegative = value => roundMark(Math.max(0, finiteNumber(value) ?? 0));
const boundMark = (value, maximum) => roundMark(Math.max(0, maximum === null ? value : Math.min(value, maximum)));
const uniqueMark = values => {
    const unique = [...new Set(values)];
    return unique.length === 1 ? unique[0] : null;
};

// Do not take the last fraction in feedback: it may be a rubric item or advice.
// An ambiguous response stays unmarked so it can be reviewed, rather than earning a fabricated zero.
export function parseAIMark(text, maxMarks, { finalOnly = false } = {}) {
    if (typeof text !== 'string') return null;
    const clean = text.replace(/<[^>]*>/g, ' ').replace(/[*_`#]/g, '').replace(/[⁄／]/g, '/');
    const maximum = finiteNumber(maxMarks);
    const number = '(-?\\d+(?:\\.\\d+)?)';
    const ratio = `(?:\\s*(?:/|out\\s+of)\\s*${number})?`;
    const groups = [
        '(?:final(?:\\s+suggested)?|overall)\\s+(?:marks?(?:\\s+awarded)?|score)',
        'total\\s+(?:marks?\\s+awarded|score)|total\\s+marks?(?!\\s+(?:available|possible|for))',
        '(?:suggested\\s+marks?|marks?\\s+awarded|score)',
    ];
    for (const label of groups.slice(0, finalOnly ? 2 : groups.length)) {
        const re = new RegExp(`\\b(?:${label})\\s*(?::|=|is|of)?\\s*${number}${ratio}`, 'gi');
        const candidates = [];
        let foundLabel = false;
        for (const match of clean.matchAll(re)) {
            foundLabel = true;
            const mark = Number(match[1]);
            const denominator = match[2] === undefined ? null : Number(match[2]);
            if (denominator !== null && (denominator <= 0 || (maximum !== null && denominator !== maximum))) continue;
            // A bare "total marks" value states a maximum as often as an award.
            if (/^total\s+marks?\b/i.test(match[0]) && !/^total\s+marks?\s+awarded\b/i.test(match[0]) && denominator === null) continue;
            candidates.push(boundMark(mark, maximum ?? denominator));
        }
        if (foundLabel) return uniqueMark(candidates);
    }
    if (finalOnly) return null;
    const candidates = [];
    const re = new RegExp(`${number}\\s*(?:/|out\\s+of)\\s*${number}`, 'gi');
    for (const match of clean.matchAll(re)) {
        const denominator = Number(match[2]);
        if (denominator <= 0 || (maximum !== null && denominator !== maximum)) continue;
        candidates.push(boundMark(Number(match[1]), maximum ?? denominator));
    }
    return uniqueMark(candidates);
}

export function createAIEvaluation(response, maxMarks) {
    const text = typeof response?.evaluationText === 'string' ? response.evaluationText : '';
    const maximum = finiteNumber(maxMarks);
    const structuredMark = finiteNumber(response?.mark);
    const mark = structuredMark === null ? parseAIMark(text, maximum) : boundMark(structuredMark, maximum);
    return {
        text: text || 'No evaluation feedback was returned.',
        mark, maxMarks: nonnegative(maxMarks), method: 'ai', markParserVersion: 1,
        status: mark === null ? 'needs-review' : 'graded',
    };
}

export function getEvaluationMark(evaluation) {
    if (!evaluation || typeof evaluation !== 'object') return null;
    if (evaluation.status === 'failed') return null;
    // Recover an explicit final award in saved feedback from older prose-only AI
    // responses. Ambiguous or incomplete feedback still requires a retry.
    if (evaluation.status === 'needs-review') return parseAIMark(evaluation.text, evaluation.maxMarks, { finalOnly: true });
    // Older AI responses used the last fraction. Recover only an explicitly labelled
    // final award, without asking AI to grade again or changing the saved feedback.
    if (!evaluation.method && !evaluation.markParserVersion) {
        const explicit = parseAIMark(evaluation.text, evaluation.maxMarks, { finalOnly: true });
        if (explicit !== null) return explicit;
    }
    const stored = finiteNumber(evaluation.mark);
    if (stored !== null) return boundMark(stored, finiteNumber(evaluation.maxMarks));
    return parseAIMark(evaluation.text, evaluation.maxMarks);
}

export function getQuestionEntry(entries, index, subIndex = null, questionId) {
    if (!entries) return undefined;
    if (subIndex !== null) return entries[index]?.[subIndex] ?? entries[`${index}-${subIndex}`] ?? entries[questionId];
    return entries[index] ?? entries[questionId];
}

export function flattenExamQuestions(questions = []) {
    return questions.flatMap((question, index) => {
        if (question.type === 'case-study') return (question.subQuestions || []).map((child, subIndex) => ({
            question: child, index, subIndex, key: `${index}-${subIndex}`,
        }));
        // Also accept the flattened snapshots used by older analytics exports.
        if (question.isCaseStudyChild) return [{ question, index: question.originalIndex, subIndex: question.subIndex, key: `${question.originalIndex}-${question.subIndex}` }];
        return [{ question, index, subIndex: null, key: String(index) }];
    });
}

export function getAttemptScores(attempt = {}) {
    const evaluations = attempt.evaluations || {};
    const entries = new Map();
    const isEvaluation = value => value && typeof value === 'object' && ('mark' in value || 'text' in value || 'status' in value);
    // Nested entries take priority over legacy flat duplicates, matching the answer review.
    Object.entries(evaluations).forEach(([parent, value]) => {
        if (value && typeof value === 'object' && !isEvaluation(value)) {
            Object.entries(value).forEach(([child, evaluation]) => {
                if (isEvaluation(evaluation)) entries.set(`${parent}-${child}`, evaluation);
            });
        }
    });
    Object.entries(evaluations).forEach(([key, value]) => {
        if (isEvaluation(value) && !entries.has(key)) entries.set(key, value);
    });
    let cents = 0;
    let pendingCount = 0;
    [...entries.keys()].sort().forEach(key => {
        const mark = getEvaluationMark(entries.get(key));
        if (mark === null) pendingCount++;
        else cents += Math.round(mark * 100);
    });
    // Aggregate-only legacy records remain readable; partial evaluations never borrow a stale total.
    const aiScore = entries.size ? cents / 100 : nonnegative(attempt.aiGradedScore);
    const autoScore = nonnegative(attempt.score);
    const autoTotal = nonnegative(attempt.autoGradedTotalMarks);
    const aiTotal = nonnegative(attempt.descriptiveTotalMarks);
    return {
        autoScore, autoTotal, aiScore, aiTotal,
        totalScore: roundMark(autoScore + aiScore),
        totalPossible: roundMark(autoTotal + aiTotal),
        pendingCount,
    };
}
